import EventSchema from '@atoms/schemas/eventSchema';
import {
  BG_DEFAULT,
  PREPR_PAGE_TYPES,
  STATIC_MAP_SECTION_ID,
} from '@constants/constants';
import useLocale from '@hooks/useLocale';
import useTranslations from '@hooks/useTranslations';
import EventInfo from '@organisms/EventInfo/EventInfo';
import Hero from '@organisms/Hero/Hero';
import HeroCtas from '@organisms/Hero/HeroCtas';
import HighlightedBannerSection from '@organisms/HighlightedBannerSection/HighlightedBannerSection';
import ImageSliderSection from '@organisms/ImageSliderSection/ImageSliderSection';
import MapSection from '@organisms/MapSection/MapSection';
import Sections from '@organisms/Sections/Sections';
import VideoSection from '@organisms/VideoSection/VideoSection';
import { EventPage as Props } from '@type-declarations/page';
import { getHeroColor } from '@utils/getHeroColor';
import { findHighlightedTag } from '@utils/getHighlightedTag';
import { useMemo } from 'react';

const translations = {
  nl: {
    eventPassed: 'Let op: Dit evenement is verlopen en niet meer actief',
  },
  en: {
    eventPassed: 'Please note: This event has expired and is no longer active',
  },
};

function EventTemplate({ page }: { page: Props }) {
  const locale = useLocale();

  const {
    id,
    title,
    introText,
    image,
    phone,
    mail,
    website,
    socials,
    alignment,
    ctaHero,
    address,
    coordinates,
    location,
    dates,
    description,
    price,
    cta,
    category,
    facilities,
    tags,
    featuredLocations,
  } = page;

  const t = useTranslations();

  const heroColor = useMemo(() => getHeroColor({ page }), [page]);

  const sortedDates = useMemo(
    () =>
      dates?.sort((a, b) => {
        if (a.until < b.until) {
          return -1;
        }
        if (a.until > b.until) {
          return 1;
        }

        return 0;
      }) || [],
    [dates],
  );

  const lastDate = !!sortedDates.length && new Date(sortedDates.at(-1)!.until);
  const todayDate = new Date();
  const isEventInPast = lastDate && lastDate.getTime() < todayDate.getTime();
  const highlightedTag = useMemo(() => findHighlightedTag(tags), [tags]);

  const actualAddress = address || location?.[0]?.address;
  const actualCoordinates = coordinates || location?.[0]?.coordinates;
  const showMap = actualAddress && actualCoordinates;

  return (
    <>
      <Hero
        pageId={id}
        title={title}
        text={introText}
        image={image}
        colorVariant={heroColor}
        containerSize="narrow"
        warning={isEventInPast ? translations[locale].eventPassed : undefined}
        showTopBar
        alignment={alignment}
      >
        <HeroCtas
          cta={ctaHero}
          mapId={showMap && STATIC_MAP_SECTION_ID}
          dates={sortedDates}
          website={website}
        />
      </Hero>
      <EventInfo
        key={id}
        dates={sortedDates}
        price={price}
        cta={cta}
        text={description}
        facilities={facilities}
      />
      {highlightedTag && (
        <HighlightedBannerSection
          icon={highlightedTag.icon}
          bannerTitle={
            highlightedTag.bannerTitle || t.highlightedBannerSectionTitle
          }
          bannerText={
            highlightedTag.bannerText || t.highlightedBannerSectionText
          }
          theme={highlightedTag.theme}
          bannerCta={highlightedTag.bannerCta}
        />
      )}

      {page.youtubeId && (
        <VideoSection
          id="event-video"
          typename="VideoSection"
          video={{ youtubeId: page.youtubeId }}
          backgroundColor={{ color: BG_DEFAULT }}
          fullWidth={false}
        />
      )}
      {page.extraImages && (
        <ImageSliderSection
          id="event-images"
          typename="ImageSliderSection"
          images={page.extraImages}
          backgroundColor={{ color: BG_DEFAULT }}
        />
      )}

      {showMap && (
        <MapSection
          id={STATIC_MAP_SECTION_ID}
          typename="MapSection"
          size="smallFixed"
          title={t.contact}
          backgroundColor={{ color: BG_DEFAULT }}
          currentLocation={{
            id: `map-pin-${id}`,
            typename: location?.[0]
              ? PREPR_PAGE_TYPES.LOCATION_PAGE
              : PREPR_PAGE_TYPES.EVENT_PAGE,
            title:
              location?.[0]?.address?.title || location?.[0]?.title || title,
            icon: category?.[0]?.icon || category?.[0]?.category?.[0]?.icon,
            pos: [
              Number(actualCoordinates.latitude),
              Number(actualCoordinates.longitude),
            ],
            address: address || location?.[0]?.address,
            image: image || location?.[0]?.image,
            slug: location?.[0]?.slug || page.slug,
          }}
          locations={featuredLocations}
          information={{
            address: actualAddress,
            phone,
            mail,
            website,
            socials,
          }}
        />
      )}

      <Sections sections={page.sections} />
      <EventSchema page={page} />
    </>
  );
}

export default EventTemplate;
